import React from 'react';

/**
 * NumericInput Component
 *
 * Reusable numeric input with increment/decrement buttons and validation
 *
 * @param {string} label - Input label
 * @param {number} value - Current value
 * @param {function} onChange - Callback when value changes
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @param {number} step - Increment/decrement step size
 * @param {string} description - Optional help text
 */
function NumericInput({
  label,
  value,
  onChange,
  min = 1,
  max = 200,
  step = 1,
  description = ''
}) {
  const handleIncrement = () => {
    const newValue = Math.min(max, value + step);
    onChange(newValue);
  };

  const handleDecrement = () => {
    const newValue = Math.max(min, value - step);
    onChange(newValue);
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;

    // Allow empty string for easier editing
    if (inputValue === '') {
      onChange('');
      return;
    }

    // Parse number
    const numValue = parseFloat(inputValue);

    // Validate
    if (!isNaN(numValue)) {
      // Clamp to min/max
      const clampedValue = Math.max(min, Math.min(max, numValue));
      onChange(clampedValue);
    }
  };

  const handleBlur = () => {
    // On blur, ensure value is valid
    if (value === '' || isNaN(value)) {
      onChange(min);
    }
  };

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-text">
        {label}
      </label>

      <div className="flex items-center gap-2">
        {/* Decrement Button */}
        <button
          type="button"
          onClick={handleDecrement}
          disabled={value <= min}
          className="w-8 h-8 flex items-center justify-center rounded bg-muted hover:bg-muted/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-text"
          aria-label="Decrease"
        >
          −
        </button>

        {/* Input Field */}
        <input
          type="number"
          value={value}
          onChange={handleInputChange}
          onBlur={handleBlur}
          min={min}
          max={max}
          step={step}
          className="flex-1 px-3 py-2 bg-background border border-muted rounded text-text text-center focus:outline-none focus:border-primary transition-colors"
        />

        {/* Increment Button */}
        <button
          type="button"
          onClick={handleIncrement}
          disabled={value >= max}
          className="w-8 h-8 flex items-center justify-center rounded bg-muted hover:bg-muted/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-text"
          aria-label="Increase"
        >
          +
        </button>
      </div>

      {/* Help Text */}
      {description && (
        <p className="text-xs text-gray-400">
          {description}
        </p>
      )}

      {/* Range Display */}
      <div className="flex justify-between text-xs text-gray-500">
        <span>Min: {min}</span>
        <span>Max: {max}</span>
      </div>
    </div>
  );
}

export default NumericInput;
